import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import apiClient from '@/lib/api-client'
import AuthLayout from '@/components/auth/AuthLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'

type Provider = 'google' | 'facebook'

export default function SocialCallback() {
  const { t } = useTranslation('auth')
  const { provider } = useParams<{ provider: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const run = async () => {
      const allowed: Provider[] = ['google', 'facebook']
      if (!provider || !allowed.includes(provider as Provider)) {
        setError(t('social.callback.errors.unsupportedProvider'))
        return
      }

      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const oauthError = searchParams.get('error')

      if (oauthError) {
        setError(searchParams.get('error_description') || oauthError)
        return
      }

      if (!code || !state) {
        setError(t('social.callback.errors.missingCode'))
        return
      }

      try {
        const response = await apiClient.get<{
          data: {
            user: unknown
            access_token: string
            refresh_token: string
            is_new_user?: boolean
          }
        }>(`/auth/social/${provider}/callback`, {
          params: { code, state },
        })

        const { access_token, refresh_token, is_new_user } = response.data.data
        localStorage.setItem('access_token', access_token)
        localStorage.setItem('refresh_token', refresh_token)
        // SSO signup skips the register plan picker — send new users to choose a plan.
        navigate(is_new_user ? '/dashboard/billing/subscription' : '/dashboard', { replace: true })
      } catch (err: any) {
        setError(
          err.response?.data?.error?.message ||
            err.response?.data?.message ||
            t('social.callback.errors.failed')
        )
      }
    }

    void run()
  }, [provider, searchParams, navigate, t])

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('social.callback.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <>
              <p className="text-sm text-destructive">{error}</p>
              <Button asChild variant="outline">
                <Link to="/login">{t('social.callback.backToLogin')}</Link>
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t('social.callback.completing')}</p>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
